import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { rupiah, tierInfo } from "@/lib/format";
import { Sparkles, Gift, Trophy, Info } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

export default function Loyalty() {
  const { user, login } = useAuth();
  const [rewards, setRewards] = useState([]);

  useEffect(() => {
    api.get("/loyalty/rewards").then((r) => setRewards(r.data)).catch(() => {});
  }, []);

  if (!user) {
    return (
      <div className="min-h-screen paper-bg">
        <Navbar />
        <div className="max-w-lg mx-auto text-center py-24 px-4">
          <Sparkles className="w-12 h-12 mx-auto text-orange-500" />
          <h2 className="mt-4 font-display font-black text-3xl">Poin Member menunggu kamu</h2>
          <p className="mt-2 text-stone-600">Masuk untuk mulai kumpulkan poin dan naik tier warga.</p>
          <button data-testid="loyalty-login-btn" onClick={login} className="mt-4 px-8 py-3 rounded-full bg-emerald-700 text-white font-bold">Masuk dengan Google</button>
        </div>
      </div>
    );
  }

  const t = tierInfo(user.tier);
  const spent = user.total_spent || 0;
  const progress = t.next ? Math.min(100, Math.round(((spent - t.prev) / (t.next - t.prev)) * 100)) : 100;

  return (
    <div className="min-h-screen paper-bg">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight">Program Poin Member</h1>
        <p className="text-stone-600 mt-1">Belanja jadi berkah, tiap Rp 1.000 = 1 Poin.</p>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-700 to-emerald-900 text-white p-8">
            <div className="flex items-center gap-3">
              <span data-testid="loyalty-tier-badge" className={`tag-pill ${t.color}`}>
                <Trophy className="w-3 h-3" /> {t.label}
              </span>
              <span className="tag-pill bg-white/10 text-white">Total belanja: {rupiah(spent)}</span>
            </div>
            <div className="mt-6">
              <div className="text-xs font-mono uppercase tracking-widest text-emerald-200/80">Saldo Poin</div>
              <div className="font-display font-black text-6xl md:text-7xl mt-1" data-testid="loyalty-points-balance">{user.poin_berkah || 0}</div>
              <div className="text-sm text-emerald-100/90">= Setara {rupiah((user.poin_berkah || 0) * 10)} diskon</div>
            </div>
            {t.next && (
              <div className="mt-6">
                <div className="text-xs font-mono uppercase tracking-widest">Menuju tier berikutnya</div>
                <Progress value={progress} className="mt-2 h-2 bg-white/20" />
                <div className="mt-1 text-xs text-emerald-100">Kurang {rupiah(t.next - spent)} untuk naik tier</div>
              </div>
            )}
          </div>

          <div className="rounded-3xl bg-amber-50 border border-amber-200 p-6">
            <Info className="w-6 h-6 text-amber-700" />
            <h3 className="mt-2 font-display font-bold text-xl">Cara Kerja</h3>
            <ul className="mt-3 space-y-2 text-sm text-stone-700">
              <li>• Belanja Rp 1.000 = <b>1 poin</b></li>
              <li>• Tukar <b>100 poin = Rp 1.000</b> diskon</li>
              <li>• Naik tier tiap Rp 150.000 / Rp 500.000 belanja</li>
              <li>• Poin tidak kadaluarsa selama warung buka</li>
            </ul>
          </div>
        </div>

        <div className="mt-8">
          <h2 className="font-display font-black text-2xl">Tukar Poin ↔ Reward</h2>
          <p className="text-sm text-stone-600">Gunakan poin di checkout untuk potongan langsung, atau intip reward spesial berikut:</p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {rewards.map((r) => {
              const canRedeem = (user.poin_berkah || 0) >= r.cost;
              return (
                <div key={r.id} data-testid={`loyalty-reward-${r.id}`} className="bg-white rounded-3xl border border-[#E5DECF] p-5 flex flex-col">
                  <Gift className="w-6 h-6 text-orange-500" />
                  <h3 className="mt-2 font-display font-bold text-lg leading-tight">{r.name}</h3>
                  <p className="text-xs text-stone-600 mt-1 flex-1">{r.desc}</p>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="tag-pill bg-amber-100 text-amber-800">{r.cost} poin</span>
                    <button
                      data-testid={`loyalty-redeem-reward-btn-${r.id}`}
                      disabled={!canRedeem}
                      onClick={() => toast.success(`Reward "${r.name}" ditandai. Kupon akan otomatis muncul di checkout berikutnya.`)}
                      className={`text-xs font-bold px-3 py-1.5 rounded-full transition ${canRedeem ? "bg-emerald-700 text-white hover:bg-emerald-800" : "bg-stone-200 text-stone-400 cursor-not-allowed"}`}
                    >
                      {canRedeem ? "Tukar" : "Kurang poin"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
